import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import GlassyModal from 'components/ui/GlassyModal';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { db } from '../../firebaseClient'; // Firebase services
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import Notification from 'components/ui/Notification';
import SimplePhoneInput from 'components/ui/SimplePhoneInput';

const ClassManagementForm = ({ isOpen, onClose, onSubmit, initialSubject = '' }) => {
  const { user, profile } = useAuth(); // Get user and profile from Firebase
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [otherSubject, setOtherSubject] = useState(false);
  const [otherClassForum, setOtherClassForum] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // Get current date for pre-filling
  const today = new Date();
  const currentDay = today.getDate();
  const currentMonth = today.getMonth() + 1; // getMonth() returns 0-11
  const currentYear = today.getFullYear();
  const { register, handleSubmit, control, formState: { errors }, reset, watch, setValue, trigger } = useForm({
    defaultValues: {
      classTitle: '',
      subject: initialSubject || '',
      custom_subject: '',
      startDateDay: currentDay.toString(),
      startDateMonth: currentMonth.toString(),
      startDateYear: currentYear.toString(),
      endDateDay: currentDay.toString(),
      endDateMonth: currentMonth.toString(),
      endDateYear: currentYear.toString(),
      classForum: '',
      custom_classForum: '',
      courseDescription: '',
      requirements: '',
      preferred_communication: '',
      phoneNumber: '',
      notes: ''
    }
  });

  // Import subjects from the categories in ExpertiseSection
  const subjects = [
    // STEM & Engineering
    'Mathematics', 'Computer Science', 'Physics', 'Chemistry', 'Engineering', 'Statistics',
    // Business & Economics
    'Business Administration', 'Economics', 'Marketing', 'Finance', 'Management', 'Accounting',
    // Humanities & Social Sciences
    'English Literature', 'History', 'Psychology', 'Sociology', 'Philosophy', 'Political Science',
    // Health & Life Sciences
    'Nursing', 'Biology', 'Medicine', 'Public Health', 'Pharmacy', 'Nutrition',
    // Other
    'Other'
  ];

  // Set otherSubject state if initialSubject is not in the subjects list
  useEffect(() => {
    if (initialSubject && !subjects.includes(initialSubject)) {
      setValue('subject', 'Other');
      setValue('custom_subject', initialSubject);
      setOtherSubject(true);
    }
  }, [initialSubject, setValue, subjects]);

  useEffect(() => {
    console.log('User:', user);
    console.log('Profile:', profile);
  }, [user, profile]);

  const communicationModes = [
    'WhatsApp', 'Discord', 'Email (Coming Soon)'
  ];

  const classForumOptions = [
    'Canvas', 'Blackboard', 'Moodle', 'Google Classroom', 'Microsoft Teams', 'Schoology', 'Edmodo', 'Other'
  ];

  const totalSteps = 3;
  const watchClassTitle = watch('classTitle');
  const watchInstitution = watch('institution');

  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const handleNextStep = async () => {
    const fieldsToValidate = {
      1: ['classTitle', 'subject', 'custom_subject', 'classForum', 'custom_classForum', 'startDateDay', 'startDateMonth', 'startDateYear', 'endDateDay', 'endDateMonth', 'endDateYear', 'preferred_communication', 'phoneNumber'],
      2: [],
    }[step];

    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      setStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleFormSubmit = async (data) => {
    console.log('ClassManagementForm - handleFormSubmit called');
    setLoading(true);
    console.log('Submitting class management request with data:', data);

    try {
      // Determine the final values for fields with "Other" option
      const finalSubject = data.subject === 'Other' ? data.custom_subject : data.subject;
      const finalClassForum = data.classForum === 'Other' ? data.custom_classForum : data.classForum;
      const finalCommunication = data.preferred_communication;

      // Combine date fields into proper dates
      const startDay = parseInt(data.startDateDay) || currentDay;
      const startMonth = parseInt(data.startDateMonth) || currentMonth;
      const startYear = parseInt(data.startDateYear) || currentYear;
      const startDate = new Date(startYear, startMonth - 1, startDay);

      const endDay = parseInt(data.endDateDay) || currentDay;
      const endMonth = parseInt(data.endDateMonth) || currentMonth;
      const endYear = parseInt(data.endDateYear) || currentYear;
      const endDate = new Date(endYear, endMonth - 1, endDay);

      const fullPhoneNumber = data.phoneNumber;

      // Save class management request to Firestore
      await addDoc(collection(db, 'class_management'), {
        user_id: user.uid,
        email: user.email,
        name: profile?.full_name || user.displayName || '',
        class_title: data.classTitle,
        subject: finalSubject,
        class_forum: finalClassForum,
        start_date: startDate,
        end_date: endDate,
        preferred_communication: finalCommunication,
        phone_number: fullPhoneNumber,
        requirements: data.requirements,
        notes: data.notes,
        status: 'pending',
        created_at: serverTimestamp()
      });

      // Call the onSubmit callback if provided
      if (onSubmit) {
        onSubmit(data);
      }

      // Show success notification
      setNotification({ 
        show: true, 
        message: 'Your class management request has been submitted successfully! We will get back to you shortly.', 
        type: 'success' 
      });
      setIsSubmitted(true);

      // Don't reset form or close modal automatically
      // Let the user close the modal manually

    } catch (error) {
      console.error('Error submitting class management request:', error);
      setNotification({ 
        show: true, 
        message: 'There was an error submitting your request. Please try again.', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-white mb-2 font-medium">Class Title <span className="text-red-400">*</span></label>
              <input
                type="text"
                {...register('classTitle', { required: 'Class title is required' })}
                className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-accent focus:border-accent"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                }}
                placeholder="Introduction to Psychology"
              />
              {errors.classTitle && <p className="mt-1 text-red-400 text-sm">{errors.classTitle.message}</p>}
            </div>

            <div>
              <label className="block text-white mb-2 font-medium">Subject <span className="text-red-400">*</span></label>
              <select
                {...register('subject', { 
                  required: 'Subject is required',
                  onChange: (e) => {
                    if (e.target.value === 'Other') {
                      setOtherSubject(true);
                    } else {
                      setOtherSubject(false);
                    }
                  }
                })}
                className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-accent focus:border-accent"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                }}
              >
                <option value="" className="bg-gray-800">Select a subject</option>
                {subjects.map(subject => (
                  <option key={subject} value={subject} className="bg-gray-800">{subject}</option>
                ))}
              </select>
              {errors.subject && <p className="mt-1 text-red-400 text-sm">{errors.subject.message}</p>}

              {otherSubject && (
                <div className="mt-3">
                  <input
                    type="text"
                    {...register('custom_subject', { 
                      required: otherSubject ? 'Please specify the subject' : false 
                    })}
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-accent focus:border-accent"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                      boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                    }}
                    placeholder="Please specify the subject"
                  />
                  {errors.custom_subject && <p className="mt-1 text-red-400 text-sm">{errors.custom_subject.message}</p>}
                </div>
              )}
            </div>

            <div>
              <label className="block text-white mb-2 font-medium">Class Forum <span className="text-red-400">*</span></label>
              <select
                {...register('classForum', { 
                  required: 'Class forum is required',
                  onChange: (e) => {
                    if (e.target.value === 'Other') {
                      setOtherClassForum(true);
                    } else {
                      setOtherClassForum(false);
                    }
                  }
                })}
                className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-accent focus:border-accent"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                }}
              >
                <option value="" className="bg-gray-800">Select class forum</option>
                {classForumOptions.map(option => (
                  <option key={option} value={option} className="bg-gray-800">{option}</option>
                ))}
              </select>
              {errors.classForum && <p className="mt-1 text-red-400 text-sm">{errors.classForum.message}</p>}

              {otherClassForum && (
                <div className="mt-3">
                  <input
                    type="text"
                    {...register('custom_classForum', { 
                      required: otherClassForum ? 'Please specify the class forum' : false 
                    })}
                    className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-accent focus:border-accent"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                      boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                    }}
                    placeholder="Please specify the class forum"
                  />
                  {errors.custom_classForum && <p className="mt-1 text-red-400 text-sm">{errors.custom_classForum.message}</p>}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white mb-2 font-medium">Start Date <span className="text-red-400">*</span></label>
                <div className="grid grid-cols-3 gap-3">
                  {/* Day Input */}
                  <div>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      step="1"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      {...register('startDateDay', { 
                        required: 'Day is required',
                        min: { value: 1, message: 'Day must be between 1-31' },
                        max: { value: 31, message: 'Day must be between 1-31' }
                      })}
                      className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center placeholder-white/50"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                      }}
                    />
                    <label className="block text-white/60 text-xs mt-1 text-center">Day</label>
                  </div>

                  {/* Month Input */}
                  <div>
                    <input
                      type="number"
                      min="1"
                      max="12"
                      step="1"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      {...register('startDateMonth', { 
                        required: 'Month is required',
                        min: { value: 1, message: 'Month must be between 1-12' },
                        max: { value: 12, message: 'Month must be between 1-12' }
                      })}
                      className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center placeholder-white/50"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                      }}
                    />
                    <label className="block text-white/60 text-xs mt-1 text-center">Month</label>
                  </div>

                  {/* Year Input */}
                  <div>
                    <input
                      type="number"
                      min={currentYear}
                      max={currentYear + 10}
                      step="1"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      {...register('startDateYear', { 
                        required: 'Year is required',
                        min: { value: currentYear, message: `Year must be ${currentYear} or later` }
                      })}
                      className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center placeholder-white/50"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                      }}
                    />
                    <label className="block text-white/60 text-xs mt-1 text-center">Year</label>
                  </div>
                </div>
                {(errors.startDateDay || errors.startDateMonth || errors.startDateYear) && (
                  <p className="mt-1 text-red-400 text-sm">Please enter a valid start date</p>
                )}
              </div>

              <div>
                <label className="block text-white mb-2 font-medium">End Date <span className="text-red-400">*</span></label>
                <div className="grid grid-cols-3 gap-3">
                  {/* Day Input */}
                  <div>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      step="1"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      {...register('endDateDay', { 
                        required: 'Day is required',
                        min: { value: 1, message: 'Day must be between 1-31' },
                        max: { value: 31, message: 'Day must be between 1-31' }
                      })}
                      className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center placeholder-white/50"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                      }}
                    />
                    <label className="block text-white/60 text-xs mt-1 text-center">Day</label>
                  </div>

                  {/* Month Input */}
                  <div>
                    <input
                      type="number"
                      min="1"
                      max="12"
                      step="1"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      {...register('endDateMonth', { 
                        required: 'Month is required',
                        min: { value: 1, message: 'Month must be between 1-12' },
                        max: { value: 12, message: 'Month must be between 1-12' }
                      })}
                      className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center placeholder-white/50"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                      }}
                    />
                    <label className="block text-white/60 text-xs mt-1 text-center">Month</label>
                  </div>

                  {/* Year Input */}
                  <div>
                    <input
                      type="number"
                      min={currentYear}
                      max={currentYear + 10}
                      step="1"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      {...register('endDateYear', { 
                        required: 'Year is required',
                        min: { value: currentYear, message: `Year must be ${currentYear} or later` }
                      })}
                      className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center placeholder-white/50"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                      }}
                    />
                    <label className="block text-white/60 text-xs mt-1 text-center">Year</label>
                  </div>
                </div>
                {(errors.endDateDay || errors.endDateMonth || errors.endDateYear) && (
                  <p className="mt-1 text-red-400 text-sm">Please enter a valid end date</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-white mb-2 font-medium">Preferred Mode of Communication <span className="text-red-400">*</span></label>
              <select
                {...register('preferred_communication', { 
                  required: 'Preferred mode of communication is required'
                })}
                className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                }}
              >
                <option value="" className="bg-gray-800 text-white">Select preferred mode</option>
                {communicationModes.map(mode => {
                  const isComingSoon = mode.includes('Coming Soon');
                  return (
                    <option 
                      key={mode} 
                      value={isComingSoon ? '' : mode} 
                      className={`bg-gray-800 ${isComingSoon ? 'text-gray-500' : 'text-white'}`}
                      disabled={isComingSoon}
                    >
                      {mode}
                    </option>
                  );
                })}
              </select>
              {errors.preferred_communication && <p className="mt-1 text-red-400 text-sm">{errors.preferred_communication.message}</p>}
            </div>

            <div>
              <label className="block text-white mb-2 font-medium">Phone Number <span className="text-red-400">*</span></label>
              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  required: 'Phone number is required',
                  validate: (value) => {
                    console.log('Phone validation - value:', value);
                    if (!value) return 'Phone number is required';
                    if (!value.startsWith('+')) return 'Please include country code (e.g., +254)';
                    if (value.length < 8) return 'Phone number too short';
                    return true;
                  }
                }}
                render={({ field: { onChange, value } }) => (
                  <SimplePhoneInput
                    value={value || ''}
                    onChange={onChange}
                    error={errors.phoneNumber?.message}
                  />
                )}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-white mb-2 font-medium">Special Requirements</label>
              <textarea
                {...register('requirements')}
                rows={4}
                className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-accent focus:border-accent"
                placeholder="Any special requirements for managing this class..."
              ></textarea>
            </div>

            <div>
              <label className="block text-white mb-2 font-medium">Notes</label>
              <textarea
                {...register('notes')}
                rows={4}
                className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-accent focus:border-accent"
                placeholder="Any additional notes or information that might be helpful..."
              ></textarea>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-white mb-2 font-medium">Special Requirements</label>
              <textarea
                {...register('requirements')}
                rows={4}
                className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-accent focus:border-accent"
                placeholder="Any special requirements for managing this class..."
              ></textarea>
            </div>

            <div>
              <label className="block text-white mb-2 font-medium">Notes</label>
              <textarea
                {...register('notes')}
                rows={4}
                className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-accent focus:border-accent"
                placeholder="Any additional notes or information that might be helpful..."
              ></textarea>
            </div>

            <div className="bg-white/5 rounded-lg p-6 mt-6">
              <h4 className="text-white text-lg font-medium mb-4">Class Management Summary</h4>

              <div>
                <p className="text-white/60 text-sm">Class Title</p>
                <p className="text-white font-medium">{watch('classTitle')}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4 mt-4">
                <div>
                  <p className="text-white/60 text-sm">Subject</p>
                  <p className="text-white font-medium">
                    {watch('subject') === 'Other' ? watch('custom_subject') : watch('subject')}
                  </p>
                </div>
                <div>
                  <p className="text-white/60 text-sm">Class Forum</p>
                  <p className="text-white font-medium">
                    {watch('classForum') === 'Other' ? watch('custom_classForum') : watch('classForum')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-white/60 text-sm">Preferred Communication</p>
                  <p className="text-white font-medium">{watch('preferred_communication')}</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm">Phone Number</p>
                  <p className="text-white font-medium">{watch('phoneNumber')}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-white/60 text-sm">Start Date</p>
                  <p className="text-white font-medium">
                    {(() => {
                      const day = watch('startDateDay');
                      const month = watch('startDateMonth');
                      const year = watch('startDateYear');
                      const monthNames = [
                        'January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'
                      ];
                      if (day && month && year) {
                        const monthIndex = parseInt(month) - 1;
                        return `${day} ${monthNames[monthIndex]} ${year}`;
                      }
                      return 'Not set';
                    })()}
                  </p>
                </div>
                <div>
                  <p className="text-white/60 text-sm">End Date</p>
                  <p className="text-white font-medium">
                    {(() => {
                      const day = watch('endDateDay');
                      const month = watch('endDateMonth');
                      const year = watch('endDateYear');
                      const monthNames = [
                        'January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'
                      ];
                      if (day && month && year) {
                        const monthIndex = parseInt(month) - 1;
                        return `${day} ${monthNames[monthIndex]} ${year}`;
                      }
                      return 'Not set';
                    })()}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 rounded-lg p-6">
              <h4 className="text-white text-lg font-medium mb-4">Pricing</h4>
              <div className="flex justify-between mb-2">
                <span className="text-white/80">Base price</span>
                <span className="text-white font-medium">$199.00</span>
              </div>
              <div className="border-t border-white/10 mt-4 pt-4 flex justify-between">
                <span className="text-white font-medium">Total</span>
                <span className="text-accent font-bold text-xl">$199.00</span>
              </div>
              <p className="text-white/60 text-sm mt-2">
                This is an estimated price. Final pricing may vary based on course complexity and duration.
              </p>
            </div>

            {isSubmitted && (
              <div className="bg-white/5 rounded-lg p-6">
                <div className="flex items-center text-purple-500 mb-2">
                  <Icon name="Info" size={20} className="mr-2" />
                  <h4 className="text-white text-lg font-medium">Thank you for your submission!</h4>
                </div>
                <p className="text-white/80">
                  Our team is reviewing your class management details and will get back to you shortly.
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <GlassyModal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        reset();
        setStep(1);
        setIsSubmitted(false);
      }}
      title="Class Management Request"
      icon="BookOpen"
      maxWidth="max-w-3xl"
    >
      <form onSubmit={handleSubmit(handleFormSubmit, (errors) => {
        console.log('Form submission failed with errors:', errors);
      })} onClick={(e) => e.stopPropagation()}>
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <React.Fragment key={index}>
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    step > index + 1 ? 'bg-purple-500 text-white' : 
                    step === index + 1 ? 'bg-white/20 text-white border-2 border-purple-500' : 
                    'bg-white/10 text-white/60'
                  }`}
                >
                  {step > index + 1 ? <Icon name="Check" size={16} /> : index + 1}
                </div>
                {index < totalSteps - 1 && (
                  <div className={`flex-1 h-1 ${step > index + 1 ? 'bg-purple-500' : 'bg-white/10'}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-white/60 text-sm">Course Details</span>
            <span className="text-white/60 text-sm">Schedule</span>
            <span className="text-white/60 text-sm">Requirements</span>
          </div>
        </div>

        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-4 border-t border-white/10">
          {step > 1 ? (
            <button
              type="button"
              onClick={handlePrevStep}
              className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white flex items-center"
            >
              <Icon name="ArrowLeft" size={18} className="mr-2" />
              Back
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white"
            >
              Cancel
            </button>
          )}

          {step < totalSteps ? (
            <button
              type="button"
              onClick={handleNextStep}
              className="px-6 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-white flex items-center"
            >
              {step === totalSteps - 1 ? 'Finish' : 'Next'}
              <Icon name="ArrowRight" size={18} className="ml-2" />
            </button>
          ) : isSubmitted ? (
            <button
              type="button"
              onClick={() => {
                onClose();
                reset();
                setStep(1);
                setIsSubmitted(false);
              }}
              className="px-6 py-2 bg-accent hover:bg-accent-600 rounded-lg text-white flex items-center justify-center"
            >
              Close
            </button>
          ) : loading ? (
            <button
              type="button"
              disabled
              className="px-6 py-2 bg-accent-600 cursor-not-allowed rounded-lg text-white flex items-center justify-center opacity-80"
            >
              <Icon name="Loader2" size={18} className="animate-spin mr-2" />
              Submitting...
            </button>
          ) : (
            <button
              type="submit"
                      className="px-6 py-2 bg-accent hover:bg-accent-600 rounded-lg text-white flex items-center"
            >
              Submit Request
              <Icon name="Send" size={18} className="ml-2" />
            </button>
          )}
        </div>
      </form>
      
      {notification.show && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ show: false, message: '', type: '' })}
        />
      )}
    </GlassyModal>
  );
};

export default ClassManagementForm;
